import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getWorkerByContact } from "../services/worker";
import {
  getAttendanceReport,
  registerFirstEntrance,
  registerExit,
} from "../services/attendance";

// Ported from sge_infantil_web/src/pages/user/worker-attendance — the
// "Marcar Presença" link on the login page. Lets a worker who doesn't have
// (or doesn't want to use) a system login identify themselves by contact
// number and mark their own entry/exit for the day, without signing in.
//
// The original screen also had a WhatsApp OTP step, but it was dead code
// there (handleContactSubmit went straight to the presence step without
// ever setting step: 'otp'), so it's intentionally left out here — this
// mirrors what the feature actually does today, not the unused half of it.
const todayIso = () => new Date().toISOString().split("T")[0];

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const MarcarPresencaLayer = () => {
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [worker, setWorker] = useState(null);
  const [todayRecord, setTodayRecord] = useState(null);
  const [checkingToday, setCheckingToday] = useState(false);
  const [marking, setMarking] = useState(false);

  const fetchTodayRecord = async (w) => {
    setCheckingToday(true);
    try {
      const resp = await getAttendanceReport({
        workerId: w.id,
        startDate: todayIso(),
        endDate: todayIso(),
        sucursalId: w.sucursalId,
      });
      const employee = (resp.data || []).find((emp) => emp.workerId === w.id) || resp.data?.[0];
      const record = employee?.attendanceDetails?.find((d) => d.date === todayIso());
      setTodayRecord(record || null);
    } catch (err) {
      toast.error("Não foi possível verificar a presença de hoje.");
    } finally {
      setCheckingToday(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const trimmed = contact.trim();
    if (!trimmed || trimmed.length < 6) {
      toast.error("Introduza um contacto válido.");
      return;
    }
    setLoading(true);
    try {
      const resp = await getWorkerByContact(trimmed);
      const found = resp?.data;
      if (!found || !found.id) {
        toast.error("Nenhum funcionário cadastrado com este contacto.");
        return;
      }
      setWorker(found);
      await fetchTodayRecord(found);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Nenhum funcionário cadastrado com este contacto.");
      } else {
        toast.error(err?.response?.data?.message || "Erro ao validar contacto. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkEntrance = async () => {
    setMarking(true);
    try {
      await registerFirstEntrance({ workerId: worker.id, workerName: worker.name, sucursalId: worker.sucursalId });
      toast.success("Entrada registada com sucesso.");
      await fetchTodayRecord(worker);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Não foi possível registar a entrada.");
    } finally {
      setMarking(false);
    }
  };

  const handleMarkExit = async () => {
    setMarking(true);
    try {
      await registerExit(todayRecord.id, { sucursalId: worker.sucursalId });
      toast.success("Saída registada com sucesso.");
      await fetchTodayRecord(worker);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Não foi possível registar a saída.");
    } finally {
      setMarking(false);
    }
  };

  const changeWorker = () => {
    setWorker(null);
    setTodayRecord(null);
    setContact("");
  };

  const isComplete = todayRecord && todayRecord.entryTime && todayRecord.exitTime;
  const isIncomplete = todayRecord && todayRecord.entryTime && !todayRecord.exitTime;

  return (
    <section className='auth bg-base d-flex flex-wrap'>
      <ToastContainer />
      <div className='auth-left d-lg-block d-none'>
        <img src='assets/images/sge-pro-banner-gestao-estudantil.png' alt='SGE-PRO - Sistema de Gestão Estudantil' />
      </div>
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div className='text-center mb-32'>
            <span className='w-64-px h-64-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
              <Icon icon='solar:clock-circle-outline' />
            </span>
            <h4 className='mb-12'>Marcar Presença</h4>
            <p className='text-secondary-light text-lg mb-0'>
              {!worker
                ? "Introduza o seu número de contacto para registar a sua presença."
                : "Confirme a sua presença de hoje abaixo."}
            </p>
          </div>

          {!worker && (
            <form onSubmit={handleContactSubmit}>
              <div className='icon-field mb-16'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:phone-calling-linear' />
                </span>
                <input
                  type='text'
                  className='form-control h-56-px bg-neutral-50 radius-12'
                  placeholder='Número de contacto'
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <button
                type='submit'
                className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 d-flex align-items-center justify-content-center gap-8'
                disabled={loading}
              >
                {loading ? (
                  "A verificar..."
                ) : (
                  <>
                    Continuar <Icon icon='mdi:arrow-right' />
                  </>
                )}
              </button>
            </form>
          )}

          {worker && (
            <div>
              <div className='d-flex align-items-center gap-12 p-16 radius-12 bg-neutral-50 mb-24'>
                <span className='w-48-px h-48-px flex-shrink-0 bg-primary-100 text-primary-600 rounded-circle d-inline-flex justify-content-center align-items-center fw-bold'>
                  {initials(worker.name)}
                </span>
                <div className='flex-grow-1'>
                  <h6 className='mb-0 text-md'>{worker.name}</h6>
                  <span className='text-secondary-light text-sm'>{worker.contact || contact}</span>
                </div>
              </div>

              {checkingToday ? (
                <div className='text-center py-24 text-secondary-light'>
                  <Icon icon='svg-spinners:ring-resize' className='text-2xl mb-8 d-block mx-auto' />
                  A verificar presença de hoje...
                </div>
              ) : isComplete ? (
                <div className='text-center'>
                  <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                    <Icon icon='mdi:check-bold' />
                  </span>
                  <h6 className='text-md text-neutral-600 mb-16'>Presença de hoje já registada</h6>
                  <div className='row gy-3 mb-8'>
                    <div className='col-6'>
                      <div className='p-16 radius-8 bg-success-focus text-center'>
                        <span className='text-secondary-light text-sm d-block'>Hora de Entrada</span>
                        <strong className='text-success-main text-lg'>{todayRecord.entryTime}</strong>
                      </div>
                    </div>
                    <div className='col-6'>
                      <div className='p-16 radius-8 bg-primary-50 text-center'>
                        <span className='text-secondary-light text-sm d-block'>Hora de Saída</span>
                        <strong className='text-primary-600 text-lg'>{todayRecord.exitTime}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isIncomplete ? (
                <div>
                  <div className='p-16 radius-8 bg-success-focus text-center mb-16'>
                    <span className='text-secondary-light text-sm d-block'>Hora de Entrada</span>
                    <strong className='text-success-main text-lg'>{todayRecord.entryTime}</strong>
                  </div>
                  <button
                    type='button'
                    className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 d-flex align-items-center justify-content-center gap-8'
                    onClick={handleMarkExit}
                    disabled={marking}
                  >
                    <Icon icon='mdi:logout' className='text-lg' /> {marking ? "A registar..." : "Marcar Saída"}
                  </button>
                </div>
              ) : (
                <button
                  type='button'
                  className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 d-flex align-items-center justify-content-center gap-8'
                  onClick={handleMarkEntrance}
                  disabled={marking}
                >
                  <Icon icon='mdi:login' className='text-lg' /> {marking ? "A registar..." : "Marcar Entrada"}
                </button>
              )}

              <button
                type='button'
                className='btn btn-outline-secondary text-sm w-100 radius-12 mt-16 d-flex align-items-center justify-content-center gap-8'
                onClick={changeWorker}
              >
                <Icon icon='mdi:account-switch-outline' /> Não é você? Trocar de funcionário
              </button>
            </div>
          )}

          <div className='mt-32 text-center'>
            <Link to='/sign-in' className='text-primary-600 fw-bold d-inline-flex align-items-center gap-4'>
              <Icon icon='mdi:arrow-left' /> Voltar ao Login
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarcarPresencaLayer;
